import { PETS, RARITIES, rollPet, MAX_COPY_LEVEL } from '../data/pets.js';
import { ESSENCE_RARITIES, ESSENCE_FUSION_COST } from '../save/SaveManager.js';
import { SHARD_UNLOCKS } from '../data/shardUnlocks.js';
import { PET_SPRITES } from '../data/sprites.js';
import { drawSprite } from '../core/Sprite.js';
import { audio } from '../systems/audio/AudioSystem.js';

const BASE_SUMMON_COST = 10;
const BASE_TEN_SUMMON_COST = 100;

// Case-opening reel tuning. The winning tile sits near the end of a long
// strip so the reel visibly "spins past" plenty of pets before decelerating
// onto it (CS-case style). LAND_JITTER keeps the final stop from landing
// dead-center every time — it must stay well under half a tile's width so
// the marker still unambiguously points at the winning tile.
const REEL_LENGTH = 40;
const WIN_INDEX = 34;
const SPIN_MS = 5000;
const LAND_JITTER = 24;
const SPECIAL_RARITIES = new Set(['mythical', 'rainbow']);

/** Shop page: spends Shards on random pet summons, via SaveManager. */
export class ShopSummonMenu {
  constructor(root, save, { onBack }) {
    this.root = root;
    this.save = save;
    this.onBack = onBack;
    this.page = 'pets';
    this.el = document.createElement('div');
    this.el.className = 'menu-overlay summon-menu-overlay';
    this._render();
    this.root.appendChild(this.el);
  }

  _render() {
    if (this.page === 'essence') {
      this._essenceScrollTop = this.el.querySelector('.essence-panel')?.scrollTop ?? this._essenceScrollTop ?? 0;
    }
    this.el.innerHTML = '';

    const title = document.createElement('div');
    title.className = 'menu-title';
    title.textContent = 'SUMMON';
    this.el.appendChild(title);

    const shards = document.createElement('div');
    shards.className = 'menu-echoes';
    shards.innerHTML = `Shards: <span class="menu-shards-value">${this.save.shards}</span>`;
    this.el.appendChild(shards);

    if (this.page === 'pets') {
      const equipped = document.createElement('div');
      equipped.className = 'menu-echoes pet-equipped-summary';
      equipped.textContent = `Equipped: ${this.save.equippedPets.length}/${this.save.maxPetSlots}`;
      this.el.appendChild(equipped);
    }

    const nav = document.createElement('div');
    nav.className = 'summon-page-nav';
    for (const [id, label] of [['pets', 'Pets'], ['essence', 'Essence']]) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = `menu-button menu-button-small ${this.page === id ? 'menu-button-selected' : ''}`;
      button.textContent = label;
      button.addEventListener('click', () => { this.page = id; audio.playSfx('click'); this._render(); });
      nav.appendChild(button);
    }
    this.el.appendChild(nav);

    if (this.page === 'pets') {
      this.el.appendChild(this._buildSummonRow());
      this.el.appendChild(this._buildPetGallery());
    } else {
      const essencePanel = this._buildEssencePanel();
      this.el.appendChild(essencePanel);
      requestAnimationFrame(() => { essencePanel.scrollTop = this._essenceScrollTop ?? 0; });
    }

    const back = document.createElement('button');
    back.type = 'button';
    back.className = 'menu-button';
    back.textContent = 'Back';
    back.addEventListener('click', () => { audio.playSfx('click'); this.onBack(); });
    this.el.appendChild(back);
  }

  /** Every pull rolls normally; an already-owned result is converted to essence. */
  _buildSummonRow() {
    const row = document.createElement('div');
    row.className = 'shop-row summon-buy-row';

    const info = document.createElement('div');
    info.className = 'shop-row-info';
    info.innerHTML = `
      <div><span class="shop-row-name">Summon Pets</span></div>
      <div class="shop-row-desc">Duplicates become essence. A 10× summon costs the same as ten single summons.</div>
    `;
    row.appendChild(info);

    const single = document.createElement('button');
    single.type = 'button';
    single.className = 'menu-button menu-button-small';
    single.textContent = `1× — ${this._summonCost(1)}`;
    single.disabled = this.save.shards < this._summonCost(1);
    single.addEventListener('click', () => this._summon(1));
    row.appendChild(single);

    const ten = document.createElement('button');
    ten.type = 'button';
    ten.className = 'menu-button menu-button-small summon-ten-button';
    ten.textContent = `10× — ${this._summonCost(10)}`;
    ten.disabled = this.save.shards < this._summonCost(10);
    ten.addEventListener('click', () => this._summon(10));
    row.appendChild(ten);

    return row;
  }

  /** Base cost, discounted by the "Bargain Hunter" Shard unlock (data/shardUnlocks.js's summonDiscount) if owned. */
  _summonCost(count) {
    const base = count === 10 ? BASE_TEN_SUMMON_COST : BASE_SUMMON_COST;
    const discount = this.save.isPerkUnlocked('summonDiscount') ? SHARD_UNLOCKS.summonDiscount.value : 0;
    return Math.max(1, Math.round(base * (1 - discount)));
  }

  _summon(count) {
    const cost = this._summonCost(count);
    if (!this.save.spendShards(cost)) return;
    const pulls = [];
    const luckyOdds = this.save.isPerkUnlocked('luckySummon');
    for (let i = 0; i < count; i++) {
      const pet = rollPet({ luckyOdds });
      const result = this.save.recordPetPull(pet.id, pet.rarity);
      pulls.push({ pet, status: result.status, level: result.level });
    }
    audio.playSfx('click');
    if (count === 10) this._playTenSummonAnimation(pulls);
    else this._playCaseAnimation(pulls[0].pet, pulls[0].status, pulls[0].level);
  }

  /** Status line for a pull result — shared by every reveal UI. */
  _pullStatusText(pet, status, level) {
    const rarity = RARITIES[pet.rarity];
    if (status === 'new') return rarity.label;
    if (status === 'leveled') return `Duplicate → Level ${level}/${MAX_COPY_LEVEL}`;
    return `Maxed → 1 ${rarity.label} Essence`;
  }

  /**
   * CS-case-style reel: a long strip of pet tiles slides past a fixed
   * center marker with a long ease-out, decelerating onto the tile that was
   * already rolled. Skip drops the transition and jumps straight to the
   * same landing spot — the outcome is decided before the reel ever moves.
   */
  _playCaseAnimation(pet, status = 'new', level = 1) {
    // Drop focus from the Summon button so a Space skip can't also re-fire
    // it via the browser's native focused-button activation on keyup.
    document.activeElement?.blur?.();

    const overlay = this._buildCaseOverlay(pet);
    this.el.appendChild(overlay);

    const strip = overlay.querySelector('.summon-strip');
    const viewport = overlay.querySelector('.summon-case');
    const winTile = strip.children[WIN_INDEX];

    // Target: winning tile's center under the viewport's center marker,
    // plus a little jitter so repeat pulls don't all stop identically.
    const jitter = (Math.random() * 2 - 1) * LAND_JITTER;
    const target = winTile.offsetLeft + winTile.offsetWidth / 2 - viewport.clientWidth / 2 + jitter;

    let done = false;
    const finish = () => {
      if (done) return;
      done = true;
      strip.style.transition = 'none';
      strip.style.transform = `translateX(${-target}px)`;
      if (SPECIAL_RARITIES.has(pet.rarity)) {
        overlay.remove();
        this._playSpecialPullSequence([{ pet, status, level }], () => this._render(), 1);
      } else {
        this._showReveal(pet, overlay, status, level);
      }
    };

    strip.addEventListener('transitionend', finish);
    // Space also lands here: menuKeyNav scopes into the case overlay and
    // Space clicks the highlighted button, which is this Skip.
    overlay.querySelector('[data-action="skip"]').addEventListener('click', () => {
      audio.playSfx('click');
      finish();
    });

    // Start from rest, then hand the whole travel to one long decelerating
    // transition. The double-rAF guarantees the initial transform is
    // painted before the transition property lands, so the reel animates
    // instead of popping straight to the end.
    strip.style.transform = 'translateX(0)';
    requestAnimationFrame(() => requestAnimationFrame(() => {
      strip.style.transition = `transform ${SPIN_MS}ms cubic-bezier(0.12, 0.85, 0.25, 1)`;
      strip.style.transform = `translateX(${-target}px)`;
    }));
  }

  /** Ten-pull animation: cards deal into a grid and flip open one after another. */
  _playTenSummonAnimation(pulls) {
    const specials = pulls.filter(({ pet }) => SPECIAL_RARITIES.has(pet.rarity));
    if (specials.length > 0) {
      this._playSpecialPullSequence(specials, () => this._showTenSummonResults(pulls), 10);
      return;
    }
    this._showTenSummonResults(pulls);
  }

  _playSpecialPullSequence(pulls, onDone, summonCount) {
    let index = 0;
    const overlay = document.createElement('div');
    overlay.className = `summon-case-overlay special-pull-overlay special-pull-overlay-${pulls[0].pet.rarity}`;
    this.el.appendChild(overlay);

    const showNext = () => {
      const { pet, status: pullStatus, level: pullLevel } = pulls[index];
      const rarity = RARITIES[pet.rarity];
      overlay.className = `summon-case-overlay special-pull-overlay special-pull-overlay-${pet.rarity}`;
      overlay.innerHTML = '';
      for (let i = 0; i < 18; i++) {
        const spark = document.createElement('i');
        spark.className = 'special-pull-spark';
        spark.style.setProperty('--spark-angle', `${i * 20}deg`);
        spark.style.setProperty('--spark-delay', `${(i % 6) * 0.08}s`);
        overlay.appendChild(spark);
      }
      for (const orbit of ['one', 'two', 'three']) {
        const ring = document.createElement('i');
        ring.className = `special-pull-orbit special-pull-orbit-${orbit}`;
        overlay.appendChild(ring);
      }
      const banner = document.createElement('div');
      banner.className = `special-pull-banner special-pull-${pet.rarity}`;
      banner.textContent = `${rarity.label.toUpperCase()} DISCOVERED`;
      overlay.appendChild(banner);
      const panel = document.createElement('div');
      panel.className = `special-pull-card special-pull-card-${pet.rarity} pet-slot-rarity-${pet.rarity}`;
      const canvas = document.createElement('canvas');
      canvas.width = 128; canvas.height = 128;
      drawSprite(canvas.getContext('2d'), PET_SPRITES[pet.spriteKey], 64, 64);
      panel.appendChild(canvas);
      const name = document.createElement('div');
      name.className = 'summon-reveal-name';
      name.textContent = pet.name;
      panel.appendChild(name);
      const status = document.createElement('div');
      status.className = 'summon-reveal-rarity';
      status.style.color = rarity.color;
      status.textContent = this._pullStatusText(pet, pullStatus, pullLevel);
      panel.appendChild(status);
      const bonus = document.createElement('div');
      bonus.className = 'summon-reveal-bonus';
      bonus.textContent = pet.bonus.text;
      panel.appendChild(bonus);
      overlay.appendChild(panel);
      const next = document.createElement('button');
      next.className = 'menu-button menu-button-small special-pull-continue';
      next.textContent = index < pulls.length - 1 ? 'Next Special Pull' : (summonCount === 1 ? 'Summon Again' : 'Continue');
      next.addEventListener('click', () => {
        audio.playSfx('levelup');
        index++;
        if (index < pulls.length) showNext();
        else if (summonCount === 1) { overlay.remove(); this._summon(1); }
        else { overlay.remove(); onDone(); }
      });
      overlay.appendChild(next);
      audio.playSfx('levelup');
    };
    showNext();
  }

  _showTenSummonResults(pulls) {
    document.activeElement?.blur?.();
    const overlay = document.createElement('div');
    overlay.className = 'summon-case-overlay summon-ten-overlay';
    const title = document.createElement('div');
    title.className = 'menu-title';
    title.textContent = '10× SUMMON';
    overlay.appendChild(title);

    const grid = document.createElement('div');
    grid.className = 'summon-ten-grid';
    const cards = pulls.map(({ pet, status, level }) => {
      const card = document.createElement('div');
      card.className = 'summon-ten-card';
      card.textContent = '?';
      card.dataset.revealed = 'false';
      card.addEventListener('click', () => reveal(card, pet, status, level));
      grid.appendChild(card);
      return card;
    });
    overlay.appendChild(grid);

    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'menu-button menu-button-small';
    skip.dataset.action = 'skip';
    skip.textContent = 'Reveal All (Space)';
    overlay.appendChild(skip);
    this.el.appendChild(overlay);

    let revealed = 0;
    const reveal = (card, pet, status, level) => {
      if (card.dataset.revealed === 'true') return;
      card.dataset.revealed = 'true';
      revealed++;
      card.className = `summon-ten-card pet-slot-rarity-${pet.rarity} summon-ten-card-reveal`;
      card.innerHTML = '';
      const canvas = document.createElement('canvas');
      canvas.width = 64; canvas.height = 64;
      drawSprite(canvas.getContext('2d'), PET_SPRITES[pet.spriteKey], 32, 32);
      card.appendChild(canvas);
      const name = document.createElement('div');
      name.textContent = pet.name;
      card.appendChild(name);
      const statusLine = document.createElement('small');
      statusLine.textContent = this._pullStatusText(pet, status, level);
      statusLine.style.color = RARITIES[pet.rarity].color;
      card.appendChild(statusLine);
      if (revealed === pulls.length) {
        skip.remove();
        const actions = document.createElement('div');
        actions.className = 'summon-result-actions';
        const again = document.createElement('button');
        again.className = 'menu-button menu-button-small';
        again.textContent = 'Summon Again';
        again.disabled = this.save.shards < this._summonCost(10);
        again.addEventListener('click', () => { audio.playSfx('click'); overlay.remove(); this._summon(10); });
        actions.appendChild(again);
        const ok = document.createElement('button');
        ok.className = 'menu-button menu-button-small';
        ok.textContent = 'OK';
        ok.addEventListener('click', () => { audio.playSfx('click'); overlay.remove(); this._render(); });
        actions.appendChild(ok);
        overlay.appendChild(actions);
        audio.playSfx('levelup');
      }
    };
    const revealAll = () => cards.forEach((card, i) => reveal(card, pulls[i].pet, pulls[i].status, pulls[i].level));
    skip.addEventListener('click', () => { audio.playSfx('click'); revealAll(); });
    cards.forEach((card, i) => setTimeout(() => reveal(card, pulls[i].pet, pulls[i].status, pulls[i].level), 180 + i * 160));
  }

  /**
   * Builds the fullscreen case overlay: reel viewport + marker + a strip of
   * REEL_LENGTH tiles (decoys drawn uniformly from all pets, winner pinned
   * at WIN_INDEX), and a Skip button (Space does the same).
   */
  _buildCaseOverlay(pet) {
    const overlay = document.createElement('div');
    overlay.className = 'summon-case-overlay';

    const caseEl = document.createElement('div');
    caseEl.className = 'summon-case';

    const strip = document.createElement('div');
    strip.className = 'summon-strip';
    for (let i = 0; i < REEL_LENGTH; i++) {
      const tilePet = i === WIN_INDEX ? pet : PETS[Math.floor(Math.random() * PETS.length)];
      strip.appendChild(this._buildReelTile(tilePet));
    }
    caseEl.appendChild(strip);

    const marker = document.createElement('div');
    marker.className = 'summon-marker';
    caseEl.appendChild(marker);

    overlay.appendChild(caseEl);

    const skip = document.createElement('button');
    skip.type = 'button';
    skip.className = 'menu-button menu-button-small';
    skip.dataset.action = 'skip';
    skip.textContent = 'Skip (Space)';
    overlay.appendChild(skip);

    return overlay;
  }

  _buildReelTile(pet) {
    const rarity = RARITIES[pet.rarity];
    const tile = document.createElement('div');
    tile.className = `summon-tile pet-slot-rarity-${pet.rarity}`;

    const canvas = document.createElement('canvas');
    canvas.width = 84;
    canvas.height = 84;
    drawSprite(canvas.getContext('2d'), PET_SPRITES[pet.spriteKey], 42, 42);
    tile.appendChild(canvas);

    const label = document.createElement('div');
    label.className = 'pet-slot-label';
    label.style.color = rarity.color;
    label.textContent = pet.name;
    tile.appendChild(label);

    return tile;
  }

  /** Swaps the overlay's contents (or fills a bare one) with the won-pet reveal panel. */
  _showReveal(pet, overlay, status = 'new', level = 1) {
    const rarity = RARITIES[pet.rarity];
    audio.playSfx('levelup');

    overlay.querySelector('[data-action="skip"]')?.remove();

    const panel = document.createElement('div');
    panel.className = `summon-reveal pet-slot-rarity-${pet.rarity}`;

    const canvas = document.createElement('canvas');
    canvas.width = 84;
    canvas.height = 84;
    drawSprite(canvas.getContext('2d'), PET_SPRITES[pet.spriteKey], 42, 42);
    panel.appendChild(canvas);

    const name = document.createElement('div');
    name.className = 'summon-reveal-name';
    name.textContent = pet.name;
    panel.appendChild(name);

    const tier = document.createElement('div');
    tier.className = 'summon-reveal-rarity';
    tier.style.color = rarity.color;
    tier.textContent = this._pullStatusText(pet, status, level);
    panel.appendChild(tier);

    const bonus = document.createElement('div');
    bonus.className = 'summon-reveal-bonus';
    bonus.textContent = pet.bonus.text;
    panel.appendChild(bonus);

    const ok = document.createElement('button');
    ok.type = 'button';
    ok.className = 'menu-button menu-button-small';
    ok.textContent = 'OK';
    ok.addEventListener('click', () => {
      audio.playSfx('click');
      overlay.remove();
      this._render();
    });
    panel.appendChild(ok);

    const again = document.createElement('button');
    again.type = 'button';
    again.className = 'menu-button menu-button-small summon-again-button';
    again.textContent = 'Summon Again';
    again.disabled = this.save.shards < this._summonCost(1);
    again.addEventListener('click', () => {
      audio.playSfx('click');
      overlay.remove();
      this._summon(1);
    });
    panel.appendChild(again);

    overlay.appendChild(panel);
  }

  _buildEssencePanel() {
    const panel = document.createElement('div');
    panel.className = 'essence-panel';
    const title = document.createElement('div');
    title.className = 'pet-gallery-header';
    title.textContent = 'ESSENCE FUSION';
    panel.appendChild(title);

    for (let i = 0; i < ESSENCE_RARITIES.length; i++) {
      const rarityId = ESSENCE_RARITIES[i];
      const rarity = RARITIES[rarityId];
      const row = document.createElement('div');
      row.className = 'shop-row essence-row';
      const count = this.save.essence[rarityId] ?? 0;
      const next = ESSENCE_RARITIES[i + 1];
      row.innerHTML = `<div class="shop-row-info"><span class="shop-row-name" style="color:${rarity.color}">${rarity.label} Essence</span><span class="shop-row-desc">${count} owned</span></div>`;
      if (next) {
        const button = document.createElement('button');
        button.className = 'menu-button menu-button-small';
        button.textContent = `Fuse ${ESSENCE_FUSION_COST} → 1`;
        button.disabled = count < ESSENCE_FUSION_COST;
        button.addEventListener('click', () => {
          this._essenceScrollTop = panel.scrollTop;
          const converted = this.save.fuseEssence(rarityId);
          if (converted) { audio.playSfx('levelup'); this._render(); }
        });
        row.appendChild(button);
        const fuseAll = document.createElement('button');
        fuseAll.className = 'menu-button menu-button-small';
        fuseAll.textContent = `Fuse All (${Math.floor(count / ESSENCE_FUSION_COST)})`;
        fuseAll.disabled = count < ESSENCE_FUSION_COST;
        fuseAll.addEventListener('click', () => {
          this._essenceScrollTop = panel.scrollTop;
          const converted = this.save.fuseAllEssence(rarityId);
          if (converted) { audio.playSfx('levelup'); this._render(); }
        });
        row.appendChild(fuseAll);
      }
      panel.appendChild(row);
    }
    return panel;
  }

  /** Collection grid grouped by category — owned pets show their sprite + name with their rarity's border, unowned ones stay a "?" silhouette. */
  _buildPetGallery() {
    const gallery = document.createElement('div');
    gallery.className = 'pet-gallery-sections';

    for (const rarity of Object.values(RARITIES)) {
      const section = document.createElement('div');
      section.className = `pet-gallery-section category-${rarity.id}`;

      const header = document.createElement('div');
      header.className = 'pet-gallery-header';
      header.textContent = rarity.label;
      header.style.color = rarity.color;
      section.appendChild(header);

      const row = document.createElement('div');
      row.className = 'pet-gallery';
      for (const pet of PETS.filter((p) => p.rarity === rarity.id)) {
        row.appendChild(this._buildGallerySlot(pet));
      }
      section.appendChild(row);

      gallery.appendChild(section);
    }

    return gallery;
  }

  _buildGallerySlot(pet) {
    const owned = this.save.isPetOwned(pet.id);
    const equipped = this.save.equippedPets.includes(pet.id);

    const slot = document.createElement('div');
    slot.className = owned
      ? `pet-slot pet-slot-rarity-${pet.rarity}${equipped ? ' pet-slot-equipped' : ''}`
      : 'pet-slot pet-slot-locked';

    const canvas = document.createElement('canvas');
    canvas.width = 84;
    canvas.height = 84;
    if (owned) {
      drawSprite(canvas.getContext('2d'), PET_SPRITES[pet.spriteKey], 42, 42);
    }
    slot.appendChild(canvas);

    if (owned) {
      const petLevel = this.save.getPetLevel(pet.id);
      const levelBadge = document.createElement('div');
      levelBadge.className = 'pet-slot-level';
      levelBadge.textContent = petLevel >= MAX_COPY_LEVEL ? 'MAX' : `Lv.${petLevel}/${MAX_COPY_LEVEL}`;
      slot.appendChild(levelBadge);
    }

    const label = document.createElement('div');
    label.className = 'pet-slot-label';
    if (owned) label.style.color = RARITIES[pet.rarity].color;
    label.textContent = owned ? pet.name : '???';
    slot.appendChild(label);

    // Bonus text shows on unowned slots too — the collection screen doubles
    // as the "what am I chasing" sheet. Legendary uniques show just their
    // effect name in the cramped slot (the full description is the hover
    // tooltip here and printed in full on the summon reveal).
    const bonus = document.createElement('div');
    bonus.className = 'pet-slot-bonus';
    bonus.textContent = pet.bonus.unique ? pet.bonus.text.split(':')[0] : pet.bonus.text;
    bonus.title = pet.bonus.text;
    slot.appendChild(bonus);
    slot.tabIndex = 0;
    slot.setAttribute('role', 'button');
    slot.addEventListener('click', () => this._showPetDetails(pet));
    slot.addEventListener('keydown', (event) => { if (event.key === 'Enter' || event.key === ' ') { event.preventDefault(); this._showPetDetails(pet); } });

    return slot;
  }

  _showPetDetails(pet) {
    const owned = this.save.isPetOwned(pet.id);
    const rarity = RARITIES[pet.rarity];
    const overlay = document.createElement('div');
    overlay.className = 'pet-detail-overlay';
    const card = document.createElement('div');
    card.className = `pet-detail-card pet-slot-rarity-${pet.rarity}`;
    const canvas = document.createElement('canvas');
    canvas.width = 128; canvas.height = 128;
    drawSprite(canvas.getContext('2d'), PET_SPRITES[pet.spriteKey], 64, 64);
    card.appendChild(canvas);
    const name = document.createElement('div');
    name.className = 'summon-reveal-name';
    name.textContent = pet.name;
    card.appendChild(name);
    const tier = document.createElement('div');
    tier.className = 'summon-reveal-rarity';
    tier.style.color = rarity.color;
    tier.textContent = rarity.label;
    card.appendChild(tier);
    const bonus = document.createElement('div');
    bonus.className = 'summon-reveal-bonus';
    bonus.textContent = pet.bonus.text;
    card.appendChild(bonus);
    const level = this.save.getPetLevel(pet.id);
    const maxed = owned && level >= MAX_COPY_LEVEL;
    const state = document.createElement('div');
    state.className = 'pet-detail-state';
    if (maxed) {
      state.textContent = 'MAX';
    } else if (owned) {
      const copiesToMax = MAX_COPY_LEVEL - level;
      state.textContent = `Level ${level}/${MAX_COPY_LEVEL} — ${copiesToMax} more cop${copiesToMax === 1 ? 'y' : 'ies'} to max`;
    } else {
      state.textContent = `Requires ${this.save.getPetBuyCost(pet.id)} ${rarity.label} Essence`;
    }
    card.appendChild(state);
    if (!maxed) {
      const buyCost = this.save.getPetBuyCost(pet.id);
      const buy = document.createElement('button');
      buy.className = 'menu-button menu-button-small';
      buy.textContent = owned ? `Buy Copy — ${buyCost} Essence` : `Buy for ${buyCost} Essence`;
      buy.disabled = (this.save.essence[pet.rarity] ?? 0) < buyCost;
      buy.addEventListener('click', () => {
        if (this.save.buyPet(pet.id, pet.rarity)) {
          audio.playSfx('levelup');
          overlay.remove();
          if (owned) { this._render(); this._showPetDetails(pet); }
          else this._playPetPurchaseAnimation(pet);
        }
      });
      card.appendChild(buy);
    }
    if (owned) {
      const equipped = this.save.equippedPets.includes(pet.id);
      const slotsFull = this.save.equippedPets.length >= this.save.maxPetSlots;
      const equipButton = document.createElement('button');
      equipButton.className = 'menu-button menu-button-small';
      if (equipped) {
        equipButton.textContent = 'Unequip';
      } else if (slotsFull) {
        equipButton.textContent = 'Slots Full';
        equipButton.disabled = true;
        equipButton.title = 'Unequip a pet first';
      } else {
        equipButton.textContent = 'Equip';
      }
      equipButton.addEventListener('click', () => {
        if (equipped) this.save.unequipPet(pet.id);
        else if (!this.save.equipPet(pet.id)) return;
        audio.playSfx('click');
        overlay.remove();
        this._render();
        this._showPetDetails(pet);
      });
      card.appendChild(equipButton);
    }
    const close = document.createElement('button');
    close.className = 'menu-button menu-button-small';
    close.textContent = 'Close';
    close.addEventListener('click', () => { audio.playSfx('click'); overlay.remove(); });
    card.appendChild(close);
    overlay.appendChild(card);
    overlay.addEventListener('click', (event) => { if (event.target === overlay) overlay.remove(); });
    this.el.appendChild(overlay);
  }

  _playPetPurchaseAnimation(pet) {
    const rarity = RARITIES[pet.rarity];
    const overlay = document.createElement('div');
    overlay.className = `pet-purchase-overlay pet-purchase-${pet.rarity}`;
    overlay.innerHTML = `<div class="pet-purchase-burst">PET ACQUIRED</div>`;
    const card = document.createElement('div');
    card.className = `pet-purchase-card pet-slot-rarity-${pet.rarity}`;
    const canvas = document.createElement('canvas');
    canvas.width = 128; canvas.height = 128;
    drawSprite(canvas.getContext('2d'), PET_SPRITES[pet.spriteKey], 64, 64);
    card.appendChild(canvas);
    const name = document.createElement('div');
    name.className = 'summon-reveal-name';
    name.textContent = pet.name;
    card.appendChild(name);
    const tier = document.createElement('div');
    tier.className = 'summon-reveal-rarity';
    tier.style.color = rarity.color;
    tier.textContent = rarity.label;
    card.appendChild(tier);
    const bonus = document.createElement('div');
    bonus.className = 'summon-reveal-bonus';
    bonus.textContent = pet.bonus.text;
    card.appendChild(bonus);
    overlay.appendChild(card);
    const close = document.createElement('button');
    close.className = 'menu-button menu-button-small';
    close.textContent = 'Continue';
    close.addEventListener('click', () => { audio.playSfx('click'); overlay.remove(); this._render(); });
    overlay.appendChild(close);
    this.el.appendChild(overlay);
  }

  destroy() {
    this.el.remove();
  }
}
